import type { Environment } from '../constants';
import type {
  BillingCycle,
  IBillingProfile,
  IInvoice,
  IInvoiceInput,
  IInvoiceLine,
  IInvoicePreview,
  ISendInvoiceInput,
  ISubscription,
  ITenantBilling,
  ITenantBillingInput,
} from '../types';

/** Mirrors the API's invoicing rules on localStorage so the screens work without a backend. */
const PROFILE_KEY = 'nexus-ui.mock.billing.profile';
const BILLING_KEY = 'nexus-ui.mock.billing.tenants';
const INVOICES_KEY = 'nexus-ui.mock.billing.invoices';
const COUNTER_KEY = 'nexus-ui.mock.billing.counter';

const CYCLE_MONTHS: Record<BillingCycle, number> = { monthly: 1, quarterly: 3, annual: 12 };

export const DEFAULT_PROFILE: IBillingProfile = {
  company_name: 'openr',
  address_lines: ['3rd Floor, Capital House', '25 Chapel Street', 'Marylebone', 'London NW1 5DH'],
  phone: '0207 535 4096',
  email_from: 'billing@weareopenr.com',
  email_cc: [],
  email_reply_to: null,
  vat_number: null,
  company_number: null,
  registered_office: '3rd Floor, Capital House, 25 Chapel Street, Marylebone, London, NW1 5DH',
  bank_name: 'HSBC Bank',
  sort_code: '401160',
  account_number: '51427520',
  iban: 'GB24HBUK40116051427520',
  swift: 'HBUKGB4B',
  payment_terms_days: 28,
  vat_rate: 20,
  currency: 'GBP',
  number_prefix: '',
  next_invoice_number: 31,
  late_payment_note: 'Interest could be charged on all late payments',
  updated_at: null,
  updated_by: null,
};

const readJson = <T>(key: string, fallback: T): T => {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key: string, value: unknown) => {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable
  }
};

const round2 = (value: number) => Math.round(value * 100) / 100;
const today = () => new Date().toISOString().slice(0, 10);

export const addDays = (iso: string, days: number): string => {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

export const addMonths = (iso: string, months: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const last = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(d, last));
  return target.toISOString().slice(0, 10);
};

const daysInclusive = (start: string, end: string) =>
  Math.round(
    (new Date(`${end}T00:00:00Z`).getTime() - new Date(`${start}T00:00:00Z`).getTime()) / 86400000,
  ) + 1;

export const nextPeriod = (
  billing: Pick<ITenantBilling, 'cycle' | 'cycle_start'>,
  lastPeriodEnd: string | null,
  now = today(),
) => {
  const months = CYCLE_MONTHS[billing.cycle];
  if (lastPeriodEnd) {
    const start = addDays(lastPeriodEnd, 1);
    return { period_start: start, period_end: addDays(addMonths(start, months), -1) };
  }
  let start = billing.cycle_start;
  let end = addDays(addMonths(start, months), -1);
  while (end < now) {
    start = addDays(end, 1);
    end = addDays(addMonths(start, months), -1);
  }
  return { period_start: start, period_end: end };
};

const shortDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${Number(d)}/${Number(m)}/${y.slice(2)}`;
};

export const buildLines = (
  subscriptions: ISubscription[],
  names: Map<string, string>,
  period: { period_start: string; period_end: string },
  cycleMonths: number,
): IInvoiceLine[] => {
  const periodDays = daysInclusive(period.period_start, period.period_end);
  const label = `${shortDate(period.period_start)} to ${shortDate(period.period_end)}`;
  return subscriptions
    .filter((sub) => sub.price > 0)
    .flatMap((sub): IInvoiceLine[] => {
      const name = names.get(sub.product_code) ?? sub.product_code;
      const units = sub.quantity ?? 1;
      if (sub.period === 'one_off') {
        if (sub.starts_on < period.period_start || sub.starts_on > period.period_end) return [];
        return [
          {
            description: name,
            product_code: sub.product_code,
            quantity: units,
            unit_price: sub.price,
            amount: round2(sub.price * units),
          },
        ];
      }
      const activeStart = sub.starts_on > period.period_start ? sub.starts_on : period.period_start;
      const activeEnd =
        sub.ends_on && sub.ends_on < period.period_end ? sub.ends_on : period.period_end;
      if (activeStart > activeEnd) return [];
      const fraction = daysInclusive(activeStart, activeEnd) / periodDays;
      const perMonth = sub.period === 'annual' ? sub.price / 12 : sub.price;
      const unitPrice = round2(perMonth * cycleMonths * fraction);
      const partial = fraction < 1 ? ` (${shortDate(activeStart)} to ${shortDate(activeEnd)})` : '';
      const qty = sub.quantity !== null ? ` × ${sub.quantity}` : '';
      return [
        {
          description: `${name}, ${label}${partial}${qty}`,
          product_code: sub.product_code,
          quantity: units,
          unit_price: unitPrice,
          amount: round2(unitPrice * units),
        },
      ];
    });
};

export const totals = (lines: IInvoiceLine[], vatRate: number) => {
  const subtotal = round2(lines.reduce((sum, line) => sum + line.amount, 0));
  const vat = round2(subtotal * (vatRate / 100));
  return { subtotal, vat_amount: vat, total: round2(subtotal + vat) };
};

// ----- stores -----

export const mockBillingProfile = (): IBillingProfile => ({
  ...DEFAULT_PROFILE,
  ...readJson<Partial<IBillingProfile>>(PROFILE_KEY, {}),
});

export const mockSaveBillingProfile = (
  profile: Omit<IBillingProfile, 'updated_at' | 'updated_by'>,
): IBillingProfile => {
  const saved = { ...profile, updated_at: new Date().toISOString(), updated_by: 'user_alex' };
  writeJson(PROFILE_KEY, saved);
  return saved;
};

type BillingStore = Record<string, ITenantBilling>;
const billingId = (environment: Environment, tenant: string) => `${environment}:${tenant}`;

export const mockTenantBilling = (
  environment: Environment,
  tenant: string,
): ITenantBilling | null =>
  readJson<BillingStore>(BILLING_KEY, {})[billingId(environment, tenant)] ?? null;

export const mockSaveTenantBilling = (
  environment: Environment,
  tenant: string,
  input: ITenantBillingInput,
): ITenantBilling => {
  const store = readJson<BillingStore>(BILLING_KEY, {});
  const record: ITenantBilling = {
    tenant,
    ...input,
    updated_at: new Date().toISOString(),
    updated_by: 'user_alex',
  };
  store[billingId(environment, tenant)] = record;
  writeJson(BILLING_KEY, store);
  return record;
};

const readInvoices = () => readJson<(IInvoice & { environment: Environment })[]>(INVOICES_KEY, []);
const writeInvoices = (invoices: (IInvoice & { environment: Environment })[]) =>
  writeJson(INVOICES_KEY, invoices);

const withOverdue = (invoice: IInvoice): IInvoice => ({
  ...invoice,
  overdue: (invoice.status === 'issued' || invoice.status === 'sent') && invoice.due_on < today(),
});

export const mockInvoices = (environment: Environment, tenant?: string): IInvoice[] =>
  readInvoices()
    .filter(
      (invoice) => invoice.environment === environment && (!tenant || invoice.tenant === tenant),
    )
    .map(withOverdue)
    .sort(
      (a, b) => b.issued_on.localeCompare(a.issued_on) || b.created_at.localeCompare(a.created_at),
    );

const nextNumber = (profile: IBillingProfile): string => {
  const counter = Math.max(readJson<number>(COUNTER_KEY, 0), profile.next_invoice_number - 1);
  const used = new Set(readInvoices().map((invoice) => invoice.number));
  let candidate = counter + 1;
  while (used.has(`${profile.number_prefix}${candidate}`)) candidate += 1;
  writeJson(COUNTER_KEY, candidate);
  return `${profile.number_prefix}${candidate}`;
};

export const mockInvoicePreview = (
  environment: Environment,
  tenant: string,
  subscriptions: ISubscription[],
  names: Map<string, string>,
  params: { period_start?: string; period_end?: string; issued_on?: string },
): IInvoicePreview => {
  const profile = mockBillingProfile();
  const billing = mockTenantBilling(environment, tenant);
  const cycle = billing ?? { cycle: 'quarterly' as const, cycle_start: today() };
  const lastEnd =
    mockInvoices(environment, tenant)
      .filter((invoice) => invoice.status !== 'void')
      .map((invoice) => invoice.period_end)
      .sort()
      .pop() ?? null;
  const suggested = nextPeriod(cycle, lastEnd);
  const periodStart = params.period_start || suggested.period_start;
  const periodEnd = params.period_end || suggested.period_end;
  const issuedOn = params.issued_on || today();
  const terms = billing?.payment_terms_days ?? profile.payment_terms_days;
  const vatRate = billing?.vat_rate ?? profile.vat_rate;
  const lines = buildLines(
    subscriptions,
    names,
    { period_start: periodStart, period_end: periodEnd },
    CYCLE_MONTHS[cycle.cycle],
  );
  const counter = Math.max(readJson<number>(COUNTER_KEY, 0), profile.next_invoice_number - 1);
  return {
    period_start: periodStart,
    period_end: periodEnd,
    issued_on: issuedOn,
    due_on: addDays(issuedOn, terms),
    payment_terms_days: terms,
    vat_rate: vatRate,
    lines,
    ...totals(lines, vatRate),
    billing,
    next_number: `${profile.number_prefix}${counter + 1}`,
  };
};

export const mockCreateInvoice = (
  environment: Environment,
  tenant: string,
  displayName: string,
  input: IInvoiceInput,
): IInvoice => {
  const profile = mockBillingProfile();
  const billing = mockTenantBilling(environment, tenant);
  if (!input.lines?.length) throw { message: 'lines must be a non-empty array', status: 400 };
  if (input.number && readInvoices().some((invoice) => invoice.number === input.number)) {
    throw { message: `Invoice number ${input.number} is already used`, status: 409 };
  }
  const issuedOn = input.issued_on || today();
  const terms =
    input.payment_terms_days ?? billing?.payment_terms_days ?? profile.payment_terms_days;
  const vatRate = input.vat_rate ?? billing?.vat_rate ?? profile.vat_rate;
  const lines = input.lines.map((line) => ({
    ...line,
    unit_price: round2(line.unit_price),
    amount: round2(line.amount ?? line.quantity * line.unit_price),
  }));
  const now = new Date().toISOString();
  const billingAddress = input.billing_address ?? billing?.billing_address ?? '';
  const invoice: IInvoice & { environment: Environment } = {
    environment,
    id: `inv_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    tenant,
    number: input.number || nextNumber(profile),
    status: 'draft',
    account_number: input.account_number ?? billing?.account_number ?? null,
    billing_name: input.billing_name ?? billing?.billing_name ?? displayName,
    billing_address: billingAddress,
    customer_address: input.customer_address ?? billing?.customer_address ?? billingAddress,
    po_number: input.po_number ?? billing?.po_number ?? null,
    period_start: input.period_start,
    period_end: input.period_end,
    issued_on: issuedOn,
    due_on: input.due_on || addDays(issuedOn, terms),
    payment_terms_days: terms,
    lines,
    vat_rate: vatRate,
    ...totals(lines, vatRate),
    currency: profile.currency,
    notes: input.notes ?? null,
    pdf_key: null,
    sent_to: [],
    sent_cc: [],
    sent_at: null,
    sent_by: null,
    paid_on: null,
    voided_at: null,
    void_reason: null,
    created_at: now,
    created_by: 'user_alex',
    updated_at: now,
    updated_by: 'user_alex',
    overdue: false,
  };
  writeInvoices([...readInvoices(), invoice]);
  return withOverdue(invoice);
};

const mutate = (
  environment: Environment,
  tenant: string,
  id: string,
  change: (invoice: IInvoice) => Partial<IInvoice>,
): IInvoice => {
  const all = readInvoices();
  const index = all.findIndex(
    (invoice) =>
      invoice.environment === environment && invoice.tenant === tenant && invoice.id === id,
  );
  if (index === -1) throw { message: 'Invoice not found', status: 404 };
  const next = {
    ...all[index],
    ...change(all[index]),
    updated_at: new Date().toISOString(),
    updated_by: 'user_alex',
  };
  all[index] = next;
  writeInvoices(all);
  return withOverdue(next);
};

export const mockUpdateInvoice = (
  environment: Environment,
  tenant: string,
  id: string,
  input: Partial<IInvoiceInput>,
): IInvoice =>
  mutate(environment, tenant, id, (invoice) => {
    if (invoice.status !== 'draft') {
      throw {
        message: `Invoice ${invoice.number} is ${invoice.status}; only drafts can be edited`,
        status: 409,
      };
    }
    if (
      input.number &&
      input.number !== invoice.number &&
      readInvoices().some((other) => other.number === input.number)
    ) {
      throw { message: `Invoice number ${input.number} is already used`, status: 409 };
    }
    const lines = (input.lines ?? invoice.lines).map((line) => ({
      ...line,
      unit_price: round2(line.unit_price),
      amount: round2(line.amount ?? line.quantity * line.unit_price),
    }));
    const vatRate = input.vat_rate ?? invoice.vat_rate;
    const issuedOn = input.issued_on ?? invoice.issued_on;
    const terms = input.payment_terms_days ?? invoice.payment_terms_days;
    return {
      number: input.number ?? invoice.number,
      period_start: input.period_start ?? invoice.period_start,
      period_end: input.period_end ?? invoice.period_end,
      issued_on: issuedOn,
      payment_terms_days: terms,
      due_on: input.due_on ?? addDays(issuedOn, terms),
      lines,
      vat_rate: vatRate,
      ...totals(lines, vatRate),
      notes: input.notes === undefined ? invoice.notes : input.notes,
      account_number:
        input.account_number === undefined ? invoice.account_number : input.account_number,
      billing_name: input.billing_name ?? invoice.billing_name,
      billing_address: input.billing_address ?? invoice.billing_address,
      customer_address: input.customer_address ?? invoice.customer_address,
      po_number: input.po_number === undefined ? invoice.po_number : input.po_number,
    };
  });

export const mockIssueInvoice = (environment: Environment, tenant: string, id: string): IInvoice =>
  mutate(environment, tenant, id, (invoice) => {
    if (invoice.status !== 'draft')
      throw { message: `Invoice ${invoice.number} is ${invoice.status}`, status: 409 };
    return { status: 'issued', pdf_key: `invoices/${tenant}/${id}.pdf` };
  });

export const mockSendInvoice = (
  environment: Environment,
  tenant: string,
  id: string,
  input: ISendInvoiceInput,
): IInvoice & { message_id: string } => {
  const sent = mutate(environment, tenant, id, (invoice) => {
    if (invoice.status === 'draft')
      throw { message: 'Issue the invoice before sending it', status: 409 };
    if (invoice.status === 'void') throw { message: 'A void invoice cannot be sent', status: 409 };
    if (input.to.length === 0)
      throw {
        message: 'No recipients: add a billing contact or give addresses in `to`',
        status: 400,
      };
    const blocked = [...input.to, ...input.cc].filter(
      (email) => !email.endsWith('@weareopenr.com'),
    );
    if (environment !== 'prod' && blocked.length > 0) {
      throw {
        message: `On ${environment} invoices can only be emailed to @weareopenr.com addresses (blocked: ${blocked.join(
          ', ',
        )})`,
        status: 400,
      };
    }
    return {
      status: invoice.status === 'paid' ? 'paid' : 'sent',
      sent_to: input.to,
      sent_cc: input.cc,
      sent_at: new Date().toISOString(),
      sent_by: 'user_alex',
    };
  });
  return { ...sent, message_id: `mock-${Date.now().toString(36)}` };
};

export const mockMarkPaid = (
  environment: Environment,
  tenant: string,
  id: string,
  paidOn: string,
): IInvoice =>
  mutate(environment, tenant, id, (invoice) => {
    if (invoice.status === 'draft' || invoice.status === 'void') {
      throw { message: `Invoice ${invoice.number} is ${invoice.status}`, status: 409 };
    }
    return { status: 'paid', paid_on: paidOn || today() };
  });

export const mockVoidInvoice = (
  environment: Environment,
  tenant: string,
  id: string,
  reason: string | null,
): IInvoice =>
  mutate(environment, tenant, id, (invoice) => {
    if (invoice.status === 'paid')
      throw { message: 'A paid invoice cannot be voided; raise a credit instead', status: 409 };
    if (invoice.status === 'void') throw { message: 'Already void', status: 409 };
    return { status: 'void', voided_at: new Date().toISOString(), void_reason: reason };
  });

export const mockInvoice = (environment: Environment, tenant: string, id: string): IInvoice => {
  const found = mockInvoices(environment, tenant).find((invoice) => invoice.id === id);
  if (!found) throw { message: 'Invoice not found', status: 404 };
  return found;
};
